import { Inject, OnChanges, Optional, SimpleChanges } from "@angular/core";
import { Component, Input, Output, EventEmitter, OnInit } from "@angular/core";
import { FormGroupDirective } from "@angular/forms";
import { DropSelectOption } from "./drop-select.model";
import { FromFieldControl } from "../../../../../@dodo/material-forms/form-field-control";

@Component({
    selector: "drop-select",
    templateUrl: "./drop-select.component.html",
    styleUrls: ["./drop-select.component.scss"],
    standalone: false
})
export class DropSelectComponent extends FromFieldControl implements OnInit, OnChanges {
	// Public Variables
	public showOptions = false;
	public activeOptionIndex = -1;
	public label: string;
	public optionExists = false;
	public touched = false;
	public hasError = false;
	public showCustom = false;
	public inputValue: string;
	public customErrorMessage: string;
	public isValid = true;
	public showMeasurement = false;
	// I/O Variables
	@Input() placeholder: string;
	@Input() required: boolean;
	@Input() options: DropSelectOption[];
	@Input() defaultOption: DropSelectOption;
	@Input() disabled: boolean;
	@Input() scroll = false;
	@Output() optionSelected = new EventEmitter();
	// Custom Form Field
	@Input() noWrap = false;
	@Input() inputType = "text";
	@Input() customPlaceholder: string;
	@Input() customMeasurement: string;
	@Input() defaultValue: string;
	@Output() customEntry = new EventEmitter();

	// Constructor
	constructor(
		@Optional()
		@Inject(FormGroupDirective)
		__formGroupDirective: FormGroupDirective
	) {
		super(__formGroupDirective);
	}

	ngOnInit(): void {
		// Check if there is a default option to select and display
		if (this.defaultOption) {
			this.label = this.defaultOption.Value;
			this.optionExists = true;
			this.hasError = false;
		} else {
			this.label = this.placeholder;
		}
	}

	// OnChanges
	ngOnChanges(changes: any) {
		if (changes["defaultOption"]) {
			if (
				this.defaultOption &&
				this.defaultOption?.Key !== "custom" &&
				this.defaultOption?.Key !== "Custom" &&
				this.defaultOption?.Key !== "CUSTOM"
			) {
				this.label = this.defaultOption.Value;
				this.showCustom = false;
				this.optionExists = true;
				this.hasError = false;
			} else {
				this.defaultValue = this.defaultOption?.Value;
				if (this.defaultValue) {
					this.showCustom = true;
					this.optionExists = true;
					this.hasError = false;
					this.showCustomMeasurement();
					this.checkInputText(this.defaultValue);
				} else {
					this.showCustom = true;
					this.optionExists = true;
					this.hasError = false;
					this.inputValue = "";
					this.hideCustomMeasurement();
				}
			}
		}
	}

	/**
	 * DROP-DOWN INTERACTIONS
	 */

	// Toggle showing and hiding the options
	public toggleOptions() {
		this.showOptions = !this.showOptions;

		// Set to touched on first toggle
		if (!this.touched) {
			this.touched = true;
		}

		// Check for an error on close via button
		if (!this.showOptions) {
			this.errorCheck();
		}
	}

	// Toggle showing options on blur
	public blurToggleOptions() {
		setTimeout(() => {
			if (this.showOptions) {
				this.showOptions = !this.showOptions;
				this.errorCheck();
			}
		}, 150);
	}

	// Select an option from the drop-down
	public selectDropOption(option: DropSelectOption) {
		if (this.showCustom) {
			if (option.Value !== "Custom" && option.Value !== "custom") {
				this.showCustom = false;
				this.label = option.Value;
				this.optionExists = true;
				this.hasError = false;
			}
		} else {
			if (option.Value == "Custom" || option.Value == "custom") {
				this.showCustom = true;
				this.optionExists = true;
				this.hasError = false;
				if (this.inputValue?.length > 0) {
					this.checkInputText(this.inputValue);
				}
			} else {
				this.label = option.Value;
				this.optionExists = true;
				this.hasError = false;
			}
		}
		this.optionSelected.emit(option);
		// Hide the drop-down options
		this.toggleOptions();
	}

	/**
	 * CUSTOM INPUT INTERACTIONS
	 */
	public checkInputText(event) {
		if (event?.target) {
			// Get the value from the text-field
			this.inputValue = event.target.value;
		} else {
			this.inputValue = event;
		}

		// Check to make sure the element is touched
		if (!this.touched) {
			this.touched = true;
		}

		// Check to make sure the input is numbers only
		const regEx = /^\d+$/;
		this.isValid = regEx.test(this.inputValue);
		// If the input is valid, emit its value
		if (this.isValid) {
			this.customEntry.emit(this.inputValue);
		}

		this.errorCheck();
	}

	// Show the custom measurement writing
	public showCustomMeasurement() {
		this.showMeasurement = true;
	}

	// Hide the custom measurement writing
	public hideCustomMeasurement() {
		if (this.inputValue.length == 0) {
			this.showMeasurement = false;
		}
	}

	/**
	 * ERROR CHECKING
	 */

	// Check required error
	public errorCheck() {
		// Check for a required error in the drop-down
		if (
			this.required &&
			this.touched &&
			!this.optionExists &&
			!this.showCustom
		) {
			this.hasError = true;
			this.customErrorMessage = "Please select an option";
			return;
		}
		// Check for a required error in the text-field
		if (
			this.showCustom &&
			this.required &&
			this.touched &&
			this.inputValue?.length < 1
		) {
			this.hasError = true;
			this.customErrorMessage = this.customPlaceholder + " is required";
			return;
		}
		if (this.showCustom && this.inputValue?.length > 0 && !this.isValid) {
			this.hasError = true;
			this.customErrorMessage = "please enter a valid input";
			return;
		}
		// Default error state of false
		this.hasError = false;
	}
}
